import {
  Avatar,
  Button,
  Chip,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Typography,
} from '@mui/material'

import { ConsoleCampaignBoxFragment } from '@/clients/utoniq-core/schema'
import { styleSets } from '@/components/styles'
import { useTranslation } from '@/core'
import { useRouter } from '@/core'
import { Cell } from '@/core/architecture'

type ConsoleCampaignBoxProps = {
  campaign: ConsoleCampaignBoxFragment
}

export const ConsoleCampaignBox: Cell<ConsoleCampaignBoxProps> = ({
  campaign,
}) => {
  return (
    <ListItem
      sx={{
        mt: 0,
        pr: campaign.entryStatus === 'canNotEntry' ? 4 : '92px',
        '.MuiListItemSecondaryAction-root': {
          whiteSpace: 'pre-wrap',
          textAlign: 'center',
        },
      }}
      secondaryAction={
        <EntryStatusChip id={campaign.id} entryStatus={campaign.entryStatus} />
      }
    >
      <ListItemAvatar>
        <Avatar
          variant="square"
          sx={{
            width: 100,
            height: 64,
            borderRadius: 2,
          }}
          alt={campaign.title}
          src={campaign.thumbnail?.smallUri || ''}
        />
      </ListItemAvatar>
      <ListItemText
        sx={{ pl: 4 }}
        primary={campaign.title}
        slotProps={{
          primary: {
            variant: 'subtitle1',
            color:
              campaign.openStatus === 'ended' ? 'text.hint' : 'text.primary',
            sx: { ...styleSets.lineCamp(2) },
          },
        }}
        secondary={<OpenStatusChip openStatus={campaign.openStatus} />}
      />
    </ListItem>
  )
}

const EntryStatusChip = ({
  id,
  entryStatus,
}: {
  id: string
  entryStatus: ConsoleCampaignBoxFragment['entryStatus']
}) => {
  const { t } = useTranslation('consoleCampaigns')
  const { routerPages, generatePath } = useRouter()

  if (!entryStatus || entryStatus === 'canNotEntry') return null

  return entryStatus === 'canEntry' ? (
    <Button
      href={generatePath(routerPages.ConsoleCampaignDetail, {
        campaignId: id,
      })}
      sx={{ borderRadius: '2px', width: 76, p: 1 }}
      color="error"
    >
      <Typography variant="overline" color="white">
        {t(entryStatus)}
      </Typography>
    </Button>
  ) : (
    <Chip
      size="small"
      sx={{
        width: 76,
        height: 20,
        borderRadius: '2px',
        bgcolor: 'secondary.main',
        py: 1,
        '.MuiChip-label': {
          px: 0,
        },
      }}
      label={
        <Typography color="white" variant="overline">
          {t(entryStatus)}
        </Typography>
      }
    />
  )
}

const OpenStatusChip = ({
  openStatus,
}: {
  openStatus: ConsoleCampaignBoxFragment['openStatus']
}) => {
  const { t } = useTranslation('consoleCampaigns')
  if (!openStatus) return null

  const style = (() => {
    switch (openStatus) {
      case 'open':
        return {
          text: 'white',
          bgcolor: 'primary.dark',
        }
      case 'willOpen':
        return {
          text: 'text.secondary',
          bgcolor: 'secondary.light',
        }
      case 'ended':
      default:
        return {
          text: 'text.disabled',
          bgcolor: 'grey.100',
        }
    }
  })()

  return (
    <Chip
      size="small"
      sx={{
        height: 16,
        borderRadius: '2px',
        bgcolor: style.bgcolor,
        p: 1,
        pt: '2px',
      }}
      label={
        <Typography color={style.text} variant="overline">
          {t(openStatus)}
        </Typography>
      }
    />
  )
}
